/**
 * Converts DataPart to TextPart.
 *
 * DataParts contain structured data that needs to be converted to text
 * before being sent to AI models (which don't support DataPart natively).
 *
 * Consolidates three existing implementations:
 * 1. content-utils.ts:convertDataPartsToText() -> format: 'structured'
 * 2. DataPartSerializer.ts:UserDataPartToTextPartConverter -> format: 'json', userMessagesOnly: true
 * 3. ClientPartNormalizer.ts -> format: 'json'
 *
 * Used in pipelines:
 * - model-to-client: Convert remaining DataParts (format: 'json')
 * - client-to-model: Convert user DataParts (format: 'structured')
 * - firestore-to-model: Convert user DataParts (format: 'structured', userMessagesOnly: true)
 */

import type { CoreLogger } from '@elara/core/logger';

import type { MessagePart } from '#schemas/message-schemas.js';
import type { ProcessorContext } from '../types.js';
import type { PartResult } from './PartProcessor.js';

import { formatFracturedJson } from '@elara/core/fractured-json';

import { isDataPart } from '#schemas/message-schemas.js';

import { PartProcessor } from './PartProcessor.js';

/**
 * Configuration for DataPart to TextPart conversion.
 */
export interface DataPartToTextConfig {
  /**
   * Format style for the converted text.
   * - 'json': Simple JSON.stringify (ClientPartNormalizer behavior)
   * - 'json-pretty': JSON.stringify with indentation
   * - 'structured': Rich formatting with type/id extraction (content-utils behavior)
   * - 'minimal': Just type/name info
   */
  format: 'json' | 'json-pretty' | 'json-fractured' | 'structured' | 'minimal';

  /**
   * Only convert DataParts in user messages.
   * Matches UserDataPartToTextPartConverter behavior.
   * Requires LoadingContext with messageRole.
   */
  userMessagesOnly?: boolean;

  skip?: (part: MessagePart, context: ProcessorContext) => boolean;
}

export class DataPartToTextProcessor extends PartProcessor {
  readonly name = 'DataPartToText';

  constructor(
    private readonly config: DataPartToTextConfig = { format: 'json', skip: () => false },
    baseLogger: CoreLogger,
  ) {
    super(baseLogger);
  }

  process(part: MessagePart, context: ProcessorContext): PartResult {
    if (!isDataPart(part) || this.config.skip?.(part, context)) return { parts: [part] };
    if (this.config.userMessagesOnly && context.message?.role !== 'user') {
      return { parts: [part] };
    }
    if (part.data === null || part.data === undefined || part.data === '') return { parts: [] };

    const text = this.formatDataPart(part.data);
    // Genkit rejects empty text parts
    if (!text || !text.trim()) return { parts: [] };
    return { parts: [{ text }] };
  }

  private formatDataPart(data: unknown): string {
    switch (this.config.format) {
      case 'json': {
        return JSON.stringify(data);
      }
      case 'json-pretty': {
        return JSON.stringify(data, null, 2);
      }
      case 'json-fractured': {
        return formatFracturedJson(data);
      }
      case 'structured': {
        return this.formatStructured(data);
      }
      case 'minimal': {
        return this.formatMinimal(data);
      }
      default: {
        return JSON.stringify(data);
      }
    }
  }

  /**
   * Structured format matching content-utils.ts:convertDataPartsToText
   */
  private formatStructured(data: unknown): string {
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      if (typeof record.type === 'string' && typeof record.id === 'string') {
        const displayName = typeof record.displayName === 'string' ? record.displayName : `this ${record.type}`;
        return `[ATTACHMENT:${record.type}]\nID: ${record.id}\nName: ${displayName}`;
      } else if (typeof record.type === 'string') {
        return formatFracturedJson(data);
      }
      // Object without type/id - just mark as attachment
      return '[ATTACHMENT:DATA]';
    } else {
      this.logger.warn({ data }, '[formatStructured] Object is not an object');
      return '[ATTACHMENT:UNKNOWN]';
    }
  }

  /**
   * Minimal format for contexts where brevity is preferred.
   */
  private formatMinimal(data: unknown): string {
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      if (typeof record.type === 'string') {
        return `[${record.type}]`;
      } else {
        this.logger.warn({ data }, '[formatMinimal] Object without type');
        return '[DATA:UNKNOWN]';
      }
    } else {
      this.logger.warn({ data }, '[formatMinimal] Object is not an object');
      return '[DATA:UNKNOWN]';
    }
  }
}
